import { Button, Modal } from '@wordpress/components';
import { useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

import ImageCropper from './ImageCropper';

/**
 * This modal maintains the state of the image cropper and allows the user to cancel or confirm the crop.
 *
 * @param {Object}   props                Component props
 * @param {boolean}  props.isOpen         The modal open state
 * @param {Function} props.onRequestClose The function to close the modal
 * @param {Function} props.onConfirm      The function to confirm the modal
 * @param {number}   props.aspectRatio    The aspect ratio of the crop
 * @param {string}   props.imageUrl       The image URL
 * @param {Crop}     props.cropValue      The crop value
 * @param {string}   props.label          The label for the cropper
 * @param {boolean}  props.isCircular     Whether the crop should be circular
 *
 * @return {JSX.Element}                      The ImageCropperModal component
 */
const ImageCropperModal = ({
  isOpen,
  onRequestClose,
  onConfirm,
  aspectRatio,
  imageUrl,
  cropValue,
  label,
  isCircular,
}) => {
  const [crop, setCrop] = useState(cropValue);

  /**
   * Sets the new crop value in the modal state.
   *
   * @param {Crop} newCrop The new crop value
   */
  const handleChange = (newCrop) => {
    setCrop(newCrop);
  };

  /**
   * Confirms the new crop value and closes the modal.
   */
  const handleSave = () => {
    onConfirm(crop);
    onRequestClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal title={__('Image Cropper', 'mediapress')} onRequestClose={onRequestClose} isFullScreen>
      <ImageCropper
        imageUrl={imageUrl}
        aspectRatio={aspectRatio}
        value={cropValue}
        label={label}
        isCircular={isCircular}
        onChange={handleChange}
      />
      <div className="mediapress-draft-copies--modal-actions">
        <Button variant="primary" onClick={handleSave}>
          {__('Crop', 'default')}
        </Button>
      </div>
    </Modal>
  );
};

export default ImageCropperModal;
